// @flow
import React from "react";

import { Validation } from "../../model/validation";
import { FormInput } from "../form-input";
import { Suburb } from "../../model/suburb";

import { Button } from "../button";

import { Select } from "@reecegroup/styles/dist/react/select";

import { pushAnalyticsPublicEvent } from "../../util/maxAnalyticsEventCollector";
import { domainConfig } from "../../domain-config";

type Props = {
    email: string,
    firstname: string,
    lastname: string,
    postcode: string,
    disableSubmit: boolean,
    status: "" | "loading",
    submitButtonText: string,
    serverValidateFail: boolean,
    serverValidateMsg: ?string,
    fnameValid: Validation,
    lnameValid: Validation,
    postcodeValid: Validation,
    password1: string,
    password2: string,
    password1Valid: Validation,
    password2Valid: Validation,
    emailValid: Validation,
    isOptIn: string,
    isOptInValid: Validation,
    onQuerySuburbs: (value: string) => Promise<Suburb[]>,
    onElementChanged: (
        e: Event,
        value: string,
        type:
            | "Firstname"
            | "Lastname"
            | "Email"
            | "Password1"
            | "Password2"
            | "Postcode"
            | "IsOptIn",
    ) => any,
    onSuburbSelected: (suburb: Suburb) => any,
    onSubmit: (e: Event) => any
};

export function Register(props: Props) {
    return (
        <div className="register-form-wrapper register-form form-wrapper">
            <form
                name="registerForm"
                id="registerForm"
                onSubmit={props.onSubmit}
            >
                <div className="form-row flex flex-sb flex-wrap">
                    <FormInput
                        inputId="firstname"
                        type="name"
                        value={props.firstname}
                        wrapperCls="form-row-half"
                        label="First Name"
                        valid={props.fnameValid}
                        onChange={(e, value) =>
                            props.onElementChanged(e, value, "Firstname")
                        }
                        tealeafPIIValue="m"
                    />
                    <FormInput
                        inputId="lastname"
                        type="name"
                        value={props.lastname}
                        wrapperCls="form-row-half"
                        label="Last Name"
                        valid={props.lnameValid}
                        onChange={(e, value) =>
                            props.onElementChanged(e, value, "Lastname")
                        }
                        tealeafPIIValue="m"
                    />
                </div>
                <FormInput
                    inputId="emailnew"
                    type="email"
                    value={props.email}
                    label="Email Address"
                    valid={props.emailValid}
                    onChange={(e, value) =>
                        props.onElementChanged(e, value, "Email")
                    }
                    tealeafPIIValue="m"
                />
                <Select
                    id="postcodeSelect"
                    disableKeyInteraction={false}
                    onQuery={value =>
                        props.onQuerySuburbs(value).then(mapSuburbsToItems)
                    }
                    onSelect={item => {
                        item && props.onSuburbSelected(item.value);
                    }}
                    render={Select => {
                        return (
                            <FormInput
                                inputId="postcode"
                                type="postcode"
                                value={props.postcode}
                                label="Suburb / Postcode"
                                valid={props.postcodeValid}
                                autocomplete="new-username"
                                items={Select.items}
                                highlightedIndex={Select.highlightedIndex}
                                onItemSelected={Select.select}
                                onChange={(e, value) => {
                                    props.onElementChanged(
                                        e,
                                        value,
                                        "Postcode"
                                    );
                                    Select.query(value);
                                }}
                                tealeafPIIValue="l"
                            />
                        );
                    }}
                />
                <FormInput
                    inputId="password1"
                    type="password"
                    value={props.password1}
                    valid={props.password1Valid}
                    label={"Password"}
                    onChange={(e, value) =>
                        props.onElementChanged(e, value, "Password1")
                    }
                    tealeafPIIValue="h"
                />
                <FormInput
                    inputId="password2"
                    type="password"
                    value={props.password2}
                    valid={props.password2Valid}
                    label={"Re-enter Password"}
                    onChange={(e, value) =>
                        props.onElementChanged(e, value, "Password2")
                    }
                    tealeafPIIValue="h"
                />
                <FormInput
                    inputId="isOptIn"
                    type="IsOptIn"
                    value="optIn"
                    checked={props.isOptIn}
                    valid={props.isOptInValid}
                    label={`I'd like to sign up to receive news, tips, product information, updates and other communications from the Reece Group. I have read the <a href="/privacy" target="_blank" title="Read the Reece Group Privacy Policy (opens in a new tab)">Privacy Policy</a>.`}
                    onChange={(e, checked) =>
                        props.onElementChanged(e, checked, "IsOptIn")
                    }
                    tealeafPIIValue="l"
                />
                <div className="form-row form-text">
                    <p>
                        By proceeding, you agree to our{" "}
                        <a href={"/terms-and-conditions"}>Terms &amp; Conditions</a>{" "}
                        and <a href={"/privacy"}>Privacy Policy.</a>
                    </p>
                </div>
                {props.serverValidateFail && (
                    <div className="form-row">
                        <div
                            className="form-error-msg form-error-msg--server"
                            dangerouslySetInnerHTML={{
                                __html: props.serverValidateMsg
                            }}
                        />
                    </div>
                )}
                <div className="form-row">
                    <Button
                        id="register"
                        type="submit"
                        disabled={props.disableSubmit}
                        status={props.status}
                    >
                        Register
                    </Button>
                </div>
                <div className="form-row form-text">
                    <p>
                        Already have an account?{" "}
                        <a 
                        id="loginlink"
                        href={"/account/login"} 
                        onClick={(event) => {
                            event.preventDefault();
                            const href = event.currentTarget.getAttribute('href');
                            pushAnalyticsPublicEvent("viewLoginSignup", 
                                domainConfig.short_name.toUpperCase());
                            setTimeout(() => (window.location.href = href), 300)}
                        }>
                            Login here
                        </a>
                    </p>
                </div>
            </form>
        </div>
    );
}

function mapSuburbsToItems(suburbs: Suburb[]) {
    return suburbs.map(s => ({
        text: `${s.suburb} ${s.codeNumber}, ${s.state}`,
        value: s
    }));
}
